/**
 * The review screen's arithmetic and list surgery, kept apart from the components so it can be
 * tested on its own.
 *
 * The screen edits a copy of the receipt's lines and saves the whole list (`PUT .../lines`). A
 * line that exists on the server is sent with its `id`. A line made here is marked `isNew`, and
 * one split off another also carries `splitFrom`, so the server copies the printed text across.
 * A merged line carries the ids folded into it in `merged`. Those marks are only true until the
 * save returns, when the server's list replaces this one: see `hasUnsavedStructure`.
 */

import type { components } from "@/api/schema";
import type { Money } from "@/lib/money";

export type ReceiptLine = components["schemas"]["ReceiptLineOut"];
export type LineIn = components["schemas"]["LineIn"];
export type Bucket = { type: "household" } | { type: "business"; businessId: string };

export interface EditLine extends ReceiptLine {
  isNew?: boolean;
  splitFrom?: string;
  merged?: string[];
}

let made = 0;

/** A placeholder id for a line the server has not seen; never sent. */
function newId(): string {
  made += 1;
  return `new-${made}`;
}

/** The line as `PUT .../lines` takes it. */
export function toLineIn(line: EditLine): LineIn {
  return {
    ...(line.isNew ? {} : { id: line.id }),
    ...(line.splitFrom ? { split_from: line.splitFrom } : {}),
    merged: line.merged ?? [],
    description: line.description,
    quantity: line.quantity,
    quantity_unit: (line.quantity_unit || null) as NonNullable<LineIn["quantity_unit"]> | null,
    unit_price_minor: line.unit_price?.minor ?? null,
    line_total_minor: line.line_total.minor,
    bucket_type: line.bucket_type as LineIn["bucket_type"],
    business_id: line.business_id,
    category_id: line.category_id,
  };
}

/** Whether a save that adds or merges lines is still out: until it returns, the server has ids
 * this list does not, and saving again would add those lines twice. */
export function hasUnsavedStructure(lines: EditLine[]): boolean {
  return lines.some((line) => line.isNew || (line.merged?.length ?? 0) > 0);
}

export function bucketOf(line: ReceiptLine): Bucket | null {
  if (line.bucket_type === "household") return { type: "household" };
  if (line.bucket_type === "business" && line.business_id) {
    return { type: "business", businessId: line.business_id };
  }
  return null;
}

function withBucket(line: EditLine, bucket: Bucket): EditLine {
  const same =
    bucket.type === line.bucket_type &&
    (bucket.type === "household" || bucket.businessId === line.business_id);
  return {
    ...line,
    bucket_type: bucket.type,
    business_id: bucket.type === "business" ? bucket.businessId : null,
    // A category belongs to its bucket, so it does not survive a move to another one.
    category_id: same ? line.category_id : null,
  };
}

/** Put the lines named by `ids` in `bucket`. */
export function assign(lines: EditLine[], ids: string[], bucket: Bucket): EditLine[] {
  return lines.map((line) => (ids.includes(line.id) ? withBucket(line, bucket) : line));
}

/** Put every line that has no bucket yet in `bucket`: most receipts are one business's. */
export function assignRest(lines: EditLine[], bucket: Bucket): EditLine[] {
  return assign(
    lines,
    lines.filter((line) => !bucketOf(line)).map((line) => line.id),
    bucket,
  );
}

export function setCategory(lines: EditLine[], id: string, categoryId: string | null): EditLine[] {
  return lines.map((line) => (line.id === id ? { ...line, category_id: categoryId } : line));
}

export function edit(
  lines: EditLine[],
  id: string,
  changes: { description: string; amountMinor: number },
): EditLine[] {
  return lines.map((line) =>
    line.id === id
      ? {
          ...line,
          description: changes.description,
          line_total: { ...line.line_total, minor: changes.amountMinor },
        }
      : line,
  );
}

export function remove(lines: EditLine[], id: string): EditLine[] {
  return lines.filter((line) => line.id !== id);
}

/**
 * Take `amountMinor` off a line into a new one beside it: one line read as two things. The new
 * line carries the same printed text and bucket, and the two still add up to what was paid.
 */
export function split(lines: EditLine[], id: string, amountMinor: number): EditLine[] {
  return lines.flatMap((line) => {
    if (line.id !== id) return [line];
    const rest: EditLine = {
      ...line,
      line_total: { ...line.line_total, minor: line.line_total.minor - amountMinor },
    };
    // Where the printed text comes from: this line, or, for a line made here, whatever it was
    // made from. It folds in nothing: the lines merged into `line` stay merged into `rest`.
    const source = line.isNew ? line.splitFrom : line.id;
    const part: EditLine = {
      ...line,
      id: newId(),
      isNew: true,
      merged: [],
      ...(source ? { splitFrom: source } : {}),
      product_id: null,
      line_total: { ...line.line_total, minor: amountMinor },
    };
    return [rest, part];
  });
}

/**
 * Fold the lines named by `ids` into the first of them, in list order: a deposit into its milk, a
 * discount into its item. The money stays the same; the printed text of each is kept by the server.
 */
export function merge(lines: EditLine[], ids: string[]): EditLine[] {
  const chosen = lines.filter((line) => ids.includes(line.id));
  const [keep, ...folded] = chosen;
  if (!keep || folded.length === 0) return lines;
  const total = chosen.reduce((sum, line) => sum + line.line_total.minor, 0);
  const merged: EditLine = {
    ...keep,
    merged: [...(keep.merged ?? []), ...folded.filter((l) => !l.isNew).map((l) => l.id)],
    line_total: { ...keep.line_total, minor: total },
  };
  return lines
    .filter((line) => !folded.includes(line))
    .map((line) => (line === keep ? merged : line));
}

/** A line typed in by hand, unassigned, with nothing printed behind it. */
export function add(
  lines: EditLine[],
  line: { description: string; amountMinor: number; currency: string },
): EditLine[] {
  return [
    ...lines,
    {
      id: newId(),
      isNew: true,
      position: lines.length + 1,
      raw_text: "",
      description: line.description,
      product_id: null,
      store_product_code: "",
      quantity: null,
      quantity_unit: "",
      unit_price: null,
      line_total: { minor: line.amountMinor, currency: line.currency },
      bucket_type: "",
      business_id: null,
      category_id: null,
    },
  ];
}

export function unassignedCount(lines: ReceiptLine[]): number {
  return lines.filter((line) => !bucketOf(line)).length;
}

/**
 * Whether the receipt's figures agree, by the server's own rule (`ExtractionResult.adds_up`):
 * with a total, the lines plus tax must make it; without one, the lines must make the subtotal.
 * Recomputed here so the warning follows every edit at once, not a round trip later.
 */
export function addsUp(
  lines: ReceiptLine[],
  taxes: Money[],
  subtotal: Money | null,
  total: Money | null,
): boolean {
  const linesTotal = lines.reduce((sum, line) => sum + line.line_total.minor, 0);
  const taxTotal = taxes.reduce((sum, tax) => sum + tax.minor, 0);
  if (total) return linesTotal + taxTotal === total.minor;
  return !subtotal || linesTotal === subtotal.minor;
}

export function linesTotal(lines: ReceiptLine[], currency: string): Money {
  return { minor: lines.reduce((sum, line) => sum + line.line_total.minor, 0), currency };
}
